// Content Trends Screen

import React, { useState, useEffect } from "react";
import "./index.css";
import Loading from "../../components/Utilities/Loading/Loading";
import { GrDownload } from "react-icons/gr";
import {BarChart,Bar,XAxis,YAxis,Cell,ResponsiveContainer} from "recharts";
import { getHRContentTrends } from "../../actions/dataLoad";
import { useUserTokenSelector, useLoadingSelector } from "store/reducers";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "store/actions";
import { Calendar } from "react-calendar";

export const Sessions = () => {
  const [selectedYear, setSelectedYear] = useState("All Time");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [apiData, setApiData] = useState([]);
  const monthNames = ["All","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const yearsArray = ["All Time", "2023", "2024"];
  const userToken = useUserTokenSelector();
  const dispatch = useDispatch();
  const [selectedTimeRange, setSelectTimeRange] = useState("all");
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const timeRangeMap = new Map([
    ["Today", "today"],
    ["This Week", "7d"],
    ["This Month", "30d"],
    ["Last 3 months", "3m"],
    ["Last 6 months", "6m"],
    ["Last Year", "lastYear"],
    ["This Year", "1y"],
    ["All Time", "all"],
    ["Custom", "custom"],
  ]);
  const timeRangeArray = [
    "All Time",
    "Today",
    "This Week",
    "This Month",
    "Last 3 months",
    "Last 6 months",
    "Last Year",
    "This Year",
    "Custom",
  ];
  
  const colorMap = {
    Sales: "#7E8ED9",
    HR: "#49AF7C",
    Product: "#CBD1F0",
    Marketing: "#C4EED9",
    Tech: "#89D4AF",
    // default: "#FFE9AA",
  };
  const chartColors = Object.values(colorMap);

  const getChartDataForCategory = (categoryName) => {
    if (!apiData) return [];
    const categoryData = apiData.find((item) => item._id === categoryName);

    if (!categoryData || !categoryData.teamDistribution) {
      return [];
    }

    return Object.entries(categoryData.teamDistribution).map(
      ([team, count]) => ({
        name: team,
        value: count,
      })
    );
  };

  function mapTimeRangeToApiValue(timeRange) {
    return timeRangeMap.get(timeRange) || "all";
  }

  const handleTimeRangeChange = (e) => {
    const value = e.target.value;
    setSelectTimeRange(value);
    if (value === "custom") {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  const fetchData = async () => {
    dispatch(startLoading());
    try {
      let params = { dateRange: selectedTimeRange , customDateRange};
      const response = await getHRContentTrends(userToken, params);
      if (response && response.data && response.data.data) {
        const rawData = response.data.data;
        const transformedDataMap = new Map();

        rawData.forEach(item => {
          const category = item.videoCategory;
          if (!transformedDataMap.has(category)) {
            transformedDataMap.set(category, { _id: category, teamDistribution: {} });
          }
          const categoryEntry = transformedDataMap.get(category);

          item.departments.forEach(dept => {
            categoryEntry.teamDistribution[dept.department] = (categoryEntry.teamDistribution[dept.department] || 0) + dept.count;
          });
        });
        setApiData(Array.from(transformedDataMap.values()));
      } else {
        setApiData([]);
        console.warn("No data received from getHRContentTrends or data is in an unexpected format.");
      }
    } catch (error) {
      console.error("Failed to fetch HR content trends:", error);
      setApiData([]); 
    } finally {
      dispatch(stopLoading());
    }
    };

  useEffect(() => {
    const { startDate, endDate } = getCustomDates();
    const isCustomDateValid =
      selectedTimeRange !== "custom" || (startDate && endDate);

    if (!isCustomDateValid) return;
    fetchData();
  }, [userToken, selectedTimeRange, customDateRange]);

  const handleCustomDateChange = (date) => {
    if (Array.isArray(date)) {
      const [startDate, endDate] = date;
      setCustomDateRange([startDate, endDate]);
      if (startDate && endDate) {
        setShowCustomCalendar(false);
      }
      return;
    }
    setCustomDateRange(date);
  };

  const getCustomDates = () => {
    if (Array.isArray(customDateRange)) {
      const [startDate, endDate] = customDateRange;
      return { startDate, endDate, singleDate: startDate };
    }
    return { startDate: null, endDate: null, singleDate: customDateRange };
  };

  const renderBarChart = (data) => (
    <ResponsiveContainer width="100%" height="80%">
      <BarChart barSize={40} data={data} margin={{ top: 0, right: 5, bottom: 5, left: -25 }}>
        <XAxis
          dataKey="name"
          tick={{
            fill: "#394B42",
            fontSize: "12px",
            fontFamily: "Quicksand",
            fontWeight: 500,
          }}
          axisLine={{ stroke: "#394B42" }}
          tickLine={false}
        />
        <YAxis
          tick={{
            fill: "#394B42",
            fontSize: "12px",
            fontFamily: "Quicksand",
            fontWeight: 500,
          }}
          axisLine={{ stroke: "#394B42" }}
          tickLine={false}
        />

        <Bar dataKey="value">
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={chartColors[index % chartColors.length]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div className="content-trends-container">
        <>
        {showCustomCalendar && (
        <div
          className="challenges-overlay-background"
          onClick={() => setShowCustomCalendar(false)}
          aria-label="Close calendar"
        ></div>
      )}
          <div>
            <div className="content-trends-top">
                <div className="content-trends-filter">
                  <select
                    value={selectedTimeRange}
                    onChange={handleTimeRangeChange}
                  >
                    {timeRangeArray.map((time, index) => (
                    <option key={index} value={mapTimeRangeToApiValue(time)}>
                      {time}
                    </option>
                  ))}
                  </select>
                </div>
              <div className="content-trends-download-div"><GrDownload /><span>Download</span></div>
            </div>
            <div className="content-trends-row">
            {showCustomCalendar && (
                <div className="custom-calendar-container">
                  <Calendar
                    onChange={handleCustomDateChange}
                    value={customDateRange}
                    selectRange
                  />
                </div>
              )}
              {apiData.length > 0 ? (
                apiData.map((categoryItem, index) => (
                  <div key={categoryItem._id || index} className="content-trends-card graph">
                    <div className="content-trends-card-heading">
                      <span>{categoryItem._id} Distribution</span>
                    </div>
                    {renderBarChart(getChartDataForCategory(categoryItem._id))}
                  </div>
                ))
              ) : (
                <p>No chart data available.</p>
              )}
            </div>
          </div>
        </>
    </div>
  );
};

export default Sessions;
